import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { apiErrorMessage, authApi } from "@/lib/api";
import { validateLoginIdentifier } from "@/lib/loginIdentifier";
import { toast } from "@/lib/toast";

const COOLDOWN_KEY = "sellix_forgot_password_cooldown";
const ATTEMPTS_KEY = "sellix_forgot_password_attempts";
const MAX_ATTEMPTS = 5;
const WINDOW_MS = 30 * 60 * 1000;

function normalizeEmail(email) {
  return email.trim().toLowerCase();
}

function readCooldown(email) {
  try {
    const raw = sessionStorage.getItem(COOLDOWN_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    if (parsed?.email !== normalizeEmail(email)) return 0;
    return Math.max(0, Number(parsed.until) - Date.now());
  } catch {
    return 0;
  }
}

function writeCooldown(email, retryAfterSeconds) {
  sessionStorage.setItem(COOLDOWN_KEY, JSON.stringify({
    email: normalizeEmail(email),
    until: Date.now() + retryAfterSeconds * 1000,
  }));
}

function readAttemptState(email) {
  try {
    const raw = sessionStorage.getItem(ATTEMPTS_KEY);
    if (!raw) return { count: 0, windowStart: Date.now() };
    const parsed = JSON.parse(raw);
    if (parsed?.email !== normalizeEmail(email)) {
      return { count: 0, windowStart: Date.now() };
    }
    const windowStart = Number(parsed.windowStart) || Date.now();
    if (Date.now() - windowStart >= WINDOW_MS) {
      return { count: 0, windowStart: Date.now() };
    }
    return { count: Number(parsed.count) || 0, windowStart };
  } catch {
    return { count: 0, windowStart: Date.now() };
  }
}

function writeAttemptState(email, state) {
  sessionStorage.setItem(ATTEMPTS_KEY, JSON.stringify({
    email: normalizeEmail(email),
    count: state.count,
    windowStart: state.windowStart,
  }));
}

function bumpAttempts(email) {
  const current = readAttemptState(email);
  const next = {
    count: current.count + 1,
    windowStart: current.count === 0 ? Date.now() : current.windowStart,
  };
  writeAttemptState(email, next);
  if (next.count >= MAX_ATTEMPTS) {
    const retryAfterSec = Math.max(
      1,
      Math.ceil((next.windowStart + WINDOW_MS - Date.now()) / 1000),
    );
    writeCooldown(email, retryAfterSec);
  }
  return next.count;
}

function formatCooldown(ms) {
  const totalSec = Math.ceil(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min > 0) return `${min}m ${sec}s`;
  return `${sec}s`;
}

function attemptsRemainingMessage(attemptsRemaining) {
  if (attemptsRemaining <= 0) {
    return "You have reached the limit of 5 reset links per 30 minutes.";
  }
  const noun = attemptsRemaining === 1 ? "link" : "links";
  return `${attemptsRemaining} reset ${noun} remaining in this 30-minute window.`;
}

function submitButtonLabel({ loading, cooldownActive, rateLimited, cooldownMs }) {
  if (cooldownActive) return `Wait ${formatCooldown(cooldownMs)}`;
  if (rateLimited) return "Limit reached — try again later";
  return "Send reset link";
}

function requestAnotherLabel({ cooldownActive, rateLimited, cooldownMs }) {
  if (!rateLimited) return "Request another link";
  if (cooldownActive) return `Wait ${formatCooldown(cooldownMs)}`;
  return "Limit reached — try again later";
}

function handleForgotSubmitError(err, emailValue, setters) {
  const { setAttemptCount, setCooldownMs } = setters;
  if (err?.status === 429 || err?.code === "rate_limited") {
    const retryAfter = err?.data?.detail?.error?.retry_after_seconds || 1800;
    writeCooldown(emailValue, retryAfter);
    writeAttemptState(emailValue, {
      count: MAX_ATTEMPTS,
      windowStart: Date.now() - WINDOW_MS + retryAfter * 1000,
    });
    setAttemptCount(MAX_ATTEMPTS);
    setCooldownMs(retryAfter * 1000);
  }
  toast.error(apiErrorMessage(err, "Could not send reset link. Try again shortly."));
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cooldownMs, setCooldownMs] = useState(0);
  const [attemptCount, setAttemptCount] = useState(0);

  useEffect(() => {
    const tick = () => {
      const remaining = readCooldown(email);
      setCooldownMs(remaining);
      setAttemptCount(readAttemptState(email).count);
    };
    tick();
    if (!email) return undefined;
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [email]);

  const cooldownActive = cooldownMs > 0;
  const rateLimited = attemptCount >= MAX_ATTEMPTS || cooldownActive;
  const attemptsRemaining = Math.max(0, MAX_ATTEMPTS - attemptCount);

  const submit = async (e) => {
    e.preventDefault();
    const id = validateLoginIdentifier(email);
    if (!id.ok) {
      toast.error(id.message);
      return;
    }
    if (rateLimited) {
      toast.error(
        cooldownActive
          ? `Please wait ${formatCooldown(cooldownMs)} before requesting another reset link.`
          : "You have reached the limit of 5 reset links per 30 minutes.",
      );
      return;
    }
    setLoading(true);
    try {
      await authApi.forgot(id.value);
      const count = bumpAttempts(id.value);
      setAttemptCount(count);
      setCooldownMs(readCooldown(id.value));
      setSent(true);
    } catch (err) {
      handleForgotSubmitError(err, id.value, { setAttemptCount, setCooldownMs });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout eyebrow="Reset" headline="Forgot your password?" sub="We'll email you a link to set a new password.">
      {!sent ? (
        <form onSubmit={submit} className="space-y-4 min-w-0 w-full" data-testid="forgot-form">
          <div className="space-y-1.5 min-w-0">
            <Label htmlFor="email" className="text-[12px]">Work email</Label>
            <Input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="h-11 sm:h-10 w-full min-w-0"
              data-testid="forgot-email"
              required
            />
          </div>
          {attemptCount > 0 && !rateLimited && (
            <p className="text-[12px] text-slate-600 break-words">
              {attemptsRemainingMessage(attemptsRemaining)}
            </p>
          )}
          {rateLimited && (
            <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 break-words" data-testid="forgot-cooldown">
              {cooldownActive
                ? `Too many reset requests. You can request another link in ${formatCooldown(cooldownMs)}.`
                : "You have reached the limit of 5 reset links per 30 minutes. Please wait before trying again."}
            </p>
          )}
          <Button
            type="submit"
            className="w-full h-11 sm:h-10"
            disabled={loading || rateLimited}
            data-testid="forgot-submit"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            {submitButtonLabel({ loading, cooldownActive, rateLimited, cooldownMs })}
          </Button>
          <Link to="/login" className="inline-flex min-h-10 items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-700" data-testid="forgot-back">
            <ArrowLeft size={12} /> Back to sign in
          </Link>
        </form>
      ) : (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 min-w-0" data-testid="forgot-success">
          <div className="flex items-start gap-3 min-w-0">
            <CheckCircle2 className="text-emerald-600 mt-0.5 shrink-0" size={18} />
            <div className="min-w-0">
              <div className="text-[13px] font-medium text-slate-900">Check your inbox</div>
              <p className="mt-1 text-[12.5px] text-slate-600 break-words">
                If an account exists for <strong className="break-all">{email}</strong>, you'll receive a reset link within a few minutes.
              </p>
              <p className="mt-2 text-[12px] text-slate-500">
                You can request up to five links per 30 minutes. After that, a cooldown applies.
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-2">
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 sm:h-10"
              disabled={loading || rateLimited}
              onClick={() => setSent(false)}
              data-testid="forgot-request-another"
            >
              {requestAnotherLabel({ cooldownActive, rateLimited, cooldownMs })}
            </Button>
            <Button asChild variant="outline" className="w-full h-11 sm:h-10" data-testid="forgot-success-back">
              <Link to="/login">Back to sign in</Link>
            </Button>
          </div>
        </div>
      )}
    </AuthLayout>
  );
}
